import React, { useState, useContext, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Loader } from '@uberall/zenit';

import styles from './google-profile-section.module.css';
import sharedStyles from '../../../sharedStyles.module.css';
import Message from '../../Message';
import SelectionList from '../selection-list';
import { RequestAccessFormContext } from '../../../contexts/request-access-form-context';
import SelectionModal from '../selection-modal';
import SelectionModalLocation from '../selection-modal/selection-modal-location';
import { SelectionListItem } from '../../../contexts/types';
import { useFetchBusinessAccounts } from '../../../api/hooks/useFetchBusinessAccounts';
import { hasOnlyOneBusinessAccount, convertBusinessAccountsData } from '../utils';

const GoogleProfileSection = () => {
    const { accessId } = useParams();
    const [showAccountModal, setShowAccountModal] = useState<boolean>(false);
    const [showLocationModal, setShowLocationModal] = useState<boolean>(false);

    const { selectedAccounts, setSelectedAccounts, selectedLocations, setSelectedLocations } =
        useContext(RequestAccessFormContext);

    const {
        data,
        isFetching,
        isLoading,
        isFetchingNextPage,
        isError,
        fetchNextPage: fetchNextPageAccount,
    } = useFetchBusinessAccounts();

    useEffect(() => {
        if (data) {
            // if the user has only one business account, we will auto-select it for the user
            if (hasOnlyOneBusinessAccount(data.pages)) {
                const businessAccount = convertBusinessAccountsData({
                    businessAccounts: data.pages,
                })[0];

                setSelectedAccounts([businessAccount]);
            }
        }
    }, [data, setSelectedAccounts]);

    const onRemoveAccount = (id: string) => {
        // if a user has only one account selected and removes it, we need to reset the selected locations
        if (selectedAccounts.length === 1) {
            setSelectedLocations([]);
        }

        setSelectedAccounts(selectedAccounts.filter((account) => account.id !== id));
    };
    const onRemoveLocation = (id: string) => {
        setSelectedLocations(selectedLocations.filter((location) => location.id !== id));
    };

    const onOpenAccountModal = () => {
        setShowAccountModal(true);
    };
    const onCloseAccountModal = () => {
        setShowAccountModal(false);
    };

    const onOpenLocationModal = () => {
        setShowLocationModal(true);
    };
    const onCloseLocationModal = () => {
        setShowLocationModal(false);
    };

    const onAccountsSelected = (accounts: SelectionListItem[]) => {
        // if a user selects more than one account we need to reset the selected locations
        if (selectedAccounts.length === 1) {
            setSelectedLocations([]);
        }

        setSelectedAccounts(accounts);
    };
    const onLocationsSelected = (locations: SelectionListItem[]) => {
        setSelectedLocations(locations);
    };

    if (isError) return <Navigate to={`/${accessId}/error`} replace={true} />;

    const showAccountSelectionList = !hasOnlyOneBusinessAccount(data?.pages);
    const hasOnlyOneBusinessAccountSelected = selectedAccounts.length === 1;
    // Following determines if the Google profile location is mandatory. This is true if there is only one business account selected and the selected account does not have the ability to grant access.
    const isGoogleProfileLocationMandatory = hasOnlyOneBusinessAccountSelected && !selectedAccounts[0].canGrantAccess;

    return (
        <section className={styles.googleProfileSection}>
            <h2 className={sharedStyles.textHeadline}>
                <Message code="app.request.access.public.step.2.google.profile" />
            </h2>
            {!isFetching ? (
                <>
                    {showAccountSelectionList && (
                        <SelectionList
                            title="app.request.access.public.step.2.google.profile.account.title"
                            items={selectedAccounts}
                            removeItem={onRemoveAccount}
                            openSelectionModal={onOpenAccountModal}
                            subtitle="app.request.access.public.step.2.google.profile.account.subtitle"
                        />
                    )}
                    {hasOnlyOneBusinessAccountSelected && (
                        <SelectionList
                            title={isGoogleProfileLocationMandatory ? "app.request.access.public.step.2.google.profile.location.mandatory.title" : "app.request.access.public.step.2.google.profile.location.title"}
                            items={selectedLocations}
                            removeItem={onRemoveLocation}
                            openSelectionModal={onOpenLocationModal}
                            subtitle="app.request.access.public.step.2.google.profile.location.subtitle"
                        />
                    )}
                </>
            ) : (
                <Loader className={styles.loader} />
            )}
            <SelectionModal
                items={convertBusinessAccountsData({
                    businessAccounts: data?.pages || [],
                    selectedAccounts,
                })}
                title="app.request.access.public.step.2.google.profile.modal.account.title"
                subtitle="app.request.access.public.step.2.google.profile.modal.account.subtitle"
                onAdd={onAccountsSelected}
                onClose={onCloseAccountModal}
                fetchNextPage={fetchNextPageAccount}
                isLoading={isLoading}
                isFetchingNextPage={isFetchingNextPage}
                showModal={showAccountModal}
            />
            {hasOnlyOneBusinessAccountSelected && (
                <SelectionModalLocation
                    title="app.request.access.public.step.2.google.profile.modal.location.title"
                    subtitle="app.request.access.public.step.2.google.profile.modal.location.subtitle"
                    onAdd={onLocationsSelected}
                    onClose={onCloseLocationModal}
                    showModal={showLocationModal}
                />
            )}
        </section>
    );
};

export default GoogleProfileSection;
